import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { create529Contribution } from "../../lib/api-modules/transactions";
import { ApiError } from "../../lib/api";
import { dollarsToCents } from "../../lib/util/money";
import { ErrorMessage } from "../auth/error-message";

/**
 * Inline 529 contribution form — Phase 5 Session 5. POSTs to
 * `/v1/accounts/{account_id}/transactions` with
 * `transaction_type: "529_contribution"`. The backend validates
 * account-type coherence (must be 529 or 529_prepaid) and writes the
 * tx + audit row. The NY deduction tile re-renders against the new
 * data via TanStack Query's invalidation.
 */

export function Plan529ContributionForm({
  accountId,
}: {
  readonly accountId: string;
}) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState<string>("");
  const [contributionDate, setContributionDate] = useState<string>(today);
  const [notes, setNotes] = useState<string>("");
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const cents = dollarsToCents(amount);
      if (cents <= 0) {
        throw new Error("Contribution amount must be positive");
      }
      const trimmedNotes = notes.trim();
      return create529Contribution(accountId, {
        transaction_type: "529_contribution",
        amount_cents: cents,
        contribution_date: contributionDate,
        ...(trimmedNotes.length > 0 ? { notes: trimmedNotes } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accounts", accountId, "transactions"],
      });
      setAmount("");
      setNotes("");
      setInlineError(null);
    },
    onError: (err) => {
      setInlineError(
        err instanceof ApiError
          ? `${err.message} (code: ${err.code})`
          : err instanceof Error
            ? err.message
            : "Couldn't record contribution.",
      );
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError(null);
    mutation.mutate();
  }

  return (
    <form
      onSubmit={onSubmit}
      aria-label="Record 529 contribution"
      className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4"
    >
      <input
        type="number"
        step="0.01"
        placeholder="Amount ($)"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        disabled={mutation.isPending}
        required
        className="rounded-md border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
      />
      <input
        type="date"
        value={contributionDate}
        onChange={(e) => setContributionDate(e.target.value)}
        disabled={mutation.isPending}
        required
        className="rounded-md border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
      />
      <input
        type="text"
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={mutation.isPending}
        className="rounded-md border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
      />
      <button
        type="submit"
        disabled={mutation.isPending}
        className="rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {mutation.isPending ? "Recording…" : "Record contribution"}
      </button>
      {inlineError && (
        <div className="sm:col-span-4">
          <ErrorMessage>{inlineError}</ErrorMessage>
        </div>
      )}
    </form>
  );
}
